import { captureException } from '@sentry/nextjs'
import { atom, useAtom } from 'jotai'
import { VerifyTwitter } from '../../data/VerifyTwitter'
import bs58 from 'bs58'
import { useCallback, useState } from 'react'
import { useWallet } from '@solana/wallet-adapter-react'
import useAuth from '../../hooks/auth'

//! The order of those enum values is important.
export enum ProfileVerificationProgressState {
  SIGN_MESSAGE,
  POSTING_TWEET,
  PASTING_TWEET_URL,
  INACTIVE,
  ERROR,
  SUCCESS,
}

/**
 * Atom storing the current state of the verification progress.
 */
export const profileVerificationProgressAtom = atom<ProfileVerificationProgressState>(
  ProfileVerificationProgressState.INACTIVE
)

export const useTwitterSignature = (): {
  signature: string | null
  signMessage: () => Promise<void>
} => {
  const [twitterSignature, setTwitterSignature] = useState<string | null>(null)
  const { publicKey, signMessage } = useWallet()
  const [, setProgressState] = useAtom(profileVerificationProgressAtom)

  const sign = async (): Promise<void> => {
    if (!publicKey || !signMessage) {
      return
    }

    const msg = 'I am verifying my twitter on glass.xyz.'

    const message = new TextEncoder().encode(msg)
    const sig = await signMessage(message)
    setTwitterSignature('sig:' + bs58.encode(sig))
    setProgressState(ProfileVerificationProgressState.POSTING_TWEET)
  }

  return {
    signature: twitterSignature,
    signMessage: sign,
  }
}

export const useVerifyTweet = (link: string): (() => Promise<void>) => {
  const { profile } = useAuth()
  const [, setProgress] = useAtom(profileVerificationProgressAtom)

  const verify = useCallback(async (): Promise<void> => {
    if (link.trim() !== '') {
      const status_id = link.split('/').pop()?.split('?')[0]
      if (profile && status_id) {
        try {
          await VerifyTwitter(status_id)
          setProgress(ProfileVerificationProgressState.SUCCESS)
        } catch (e) {
          console.error(e)
          setProgress(ProfileVerificationProgressState.ERROR)
          captureException(e)
        }
      }
    }
  }, [link, profile, setProgress])

  return verify
}
